// Phase 0 / B0 deliverable 5 — the single canonical error-envelope client
// helper ([D6]). Every customer-web hook unwraps platform-graph responses
// through here; batches never hand-parse the spec-58 envelope.
//
// Three layers:
//   * `unwrapEnvelope(result)` — turns an openapi-fetch `{ data, error,
//     response }` triple into the success payload, throwing `EnvelopeError`
//     on a failure envelope or a malformed response. Codifies the spec-58
//     envelope contract (SUCCESS / FAILURE / MALFORMED branches). This is
//     the customer-web sibling of operator-console's `unwrap()`.
//   * `toUiError(error)` — maps a thrown error (EnvelopeError or otherwise)
//     into a typed `UiError` that carries the spec-58 classification, an
//     i18n key for the user-facing message, and a `surface` hint
//     (toast vs inline) so a TanStack Query error boundary or a form can
//     render it consistently.
//   * `errorMessage(error, t)` — the component-facing entry point. Resolves
//     `toUiError(error).messageKey` through the spec-58 3-tier i18n fallback
//     chain (errors.code.<code> → errors.classification.<c>.fallback →
//     errors.unknown.fallback) so an untranslated backend error code always
//     renders a classification-appropriate message instead of a bare
//     generic fallback or a raw i18n key. Components call
//     `errorMessage(err, t)` — never `t(uiError.messageKey)` directly.
//
// EnvelopeError carries the full envelope error array + meta so downstream
// consumers (toast adapter, retry logic, future step-up auth) decide on
// classification + retryability + i18n key.

import type { TFunction } from "i18next";

import type {
  Classification,
  Envelope,
  EnvelopeMeta,
  ErrorEntry,
} from "@novemai/error-envelope";

/**
 * Frontend-local synthetic error codes — emitted by `unwrapEnvelope()`
 * when the backend response either failed before reaching application
 * logic (network/CORS) or violated the envelope contract. Pinned as a
 * typed const so the throw branches + the i18n bundle's `errors.code.*`
 * keys reference the SAME string (typo drift caught at build time).
 */
export const SYNTHETIC_ERROR_CODES = {
  /** Wire never reached the server, or response wasn't parseable as an envelope. */
  NETWORK: "platform.network_error",
  /** Server returned 2xx but the body violated the spec-58 envelope shape. */
  INVARIANT: "platform.invariant_violation",
} as const;

export type SyntheticErrorCode =
  (typeof SYNTHETIC_ERROR_CODES)[keyof typeof SYNTHETIC_ERROR_CODES];

/**
 * Thrown by `unwrapEnvelope()` when the backend returns a failure envelope
 * or a malformed response. Carries the full envelope error array + meta so
 * downstream consumers can make decisions on classification + retryability
 * + i18n key.
 */
export class EnvelopeError extends Error {
  readonly errors: readonly ErrorEntry[];
  readonly meta: EnvelopeMeta | undefined;
  /** HTTP status code observed on the wire; 0 when network/CORS fails before status is known. */
  readonly httpStatus: number;

  constructor(input: {
    readonly errors: readonly ErrorEntry[];
    readonly meta?: EnvelopeMeta;
    readonly httpStatus: number;
  }) {
    const primary = input.errors[0];
    const message = primary
      ? `${primary.code}: ${primary.message}`
      : `Envelope error (HTTP ${input.httpStatus}, no errors[] entry)`;
    super(message);
    this.name = "EnvelopeError";
    this.errors = input.errors;
    this.meta = input.meta;
    this.httpStatus = input.httpStatus;
    // Restore the prototype chain — defensive when transpiling class
    // through tooling that targets older runtimes.
    Object.setPrototypeOf(this, EnvelopeError.prototype);
  }

  /** First (most relevant) error entry — most callers need only this. */
  get primary(): ErrorEntry | undefined {
    return this.errors[0];
  }

  /** Quick classification check — `if (err.is("authentication")) signoutRedirect()`. */
  is(classification: Classification): boolean {
    return this.primary?.classification === classification;
  }

  /** Code presence check — `if (err.hasCode("auth.permission_denied")) ...`. */
  hasCode(code: string): boolean {
    return this.errors.some((e) => e.code === code);
  }

  /** True iff the primary error is retryable per spec-58 retryability rules. */
  get retryable(): boolean {
    return this.primary?.retryable === true;
  }

  /** Server-suggested retry delay (ms); set on rate_limited / dependency / unavailable. */
  get retryAfterMs(): number | undefined {
    return this.primary?.retry_after_ms;
  }
}

/**
 * Shape constraint for an openapi-fetch result whose JSON body conforms to
 * the canonical envelope. `TData` is the value type at body.data on the
 * success branch — i.e. the inner shape the route called makeSuccess() with.
 */
export interface EnvelopedFetchResult<TData> {
  readonly data?: Envelope<TData>;
  readonly error?: Envelope<null>;
  readonly response?: Response;
}

/**
 * Unwrap an openapi-fetch result into the success payload, throwing
 * `EnvelopeError` on any failure (envelope or malformed). Generic over
 * `TData` so callers retain the response payload's full type from the
 * codegen pipeline (paths from src/api/schema.generated.ts).
 *
 * Usage:
 *   const data = await unwrapEnvelope(platformGraph.GET("/v1/employees"));
 *
 * Hook integration (TanStack Query):
 *   useQuery({
 *     queryKey: ["employees", "list"],
 *     queryFn: () => unwrapEnvelope(platformGraph.GET("/v1/employees")),
 *   });
 */
export async function unwrapEnvelope<TData>(
  resultPromise: Promise<EnvelopedFetchResult<TData>>,
): Promise<TData> {
  const result = await resultPromise;
  const status = result.response?.status ?? 0;

  // openapi-fetch sets `data` on 2xx and `error` on non-2xx; the body shape
  // is the SAME envelope either way. We pick whichever is present — the
  // envelope-shape contract is what matters, not which slot it landed in.
  const body = result.data ?? result.error;

  if (body === undefined) {
    // Network error / CORS reject / non-JSON / empty 204 — no envelope to
    // inspect; surface a synthetic dependency error.
    throw new EnvelopeError({
      errors: [
        {
          code: SYNTHETIC_ERROR_CODES.NETWORK,
          classification: "dependency",
          message: `HTTP ${status}: empty or non-envelope response body`,
          // Localized fallback (tr-TR primary per ADR-014 §6). The display path
          // resolves errors.code.platform.network_error via i18n; this field is
          // only the missing-bundle fallback (spec-58 feat_err_localization).
          user_message:
            "Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.",
          retryable: true,
        },
      ],
      httpStatus: status,
    });
  }

  // Failure branch: errors[] populated.
  const hasErrors = Array.isArray(body.errors) && body.errors.length > 0;
  if (hasErrors) {
    throw new EnvelopeError({
      errors: body.errors as readonly ErrorEntry[],
      ...(body.meta !== undefined ? { meta: body.meta } : {}),
      httpStatus: status,
    });
  }

  // Success branch: data must be non-null. If missing here, the response
  // violated the envelope invariant.
  if (body.data === null || body.data === undefined) {
    throw new EnvelopeError({
      errors: [
        {
          code: SYNTHETIC_ERROR_CODES.INVARIANT,
          classification: "internal",
          message: `Envelope shape invariant violated: HTTP ${status} with no data and no errors`,
          // Localized fallback (tr-TR primary per ADR-014 §6); display resolves
          // errors.code.platform.invariant_violation via i18n.
          user_message:
            "Bir şeyler ters gitti. Lütfen tekrar deneyin veya destek ekibine başvurun.",
          retryable: false,
        },
      ],
      ...(body.meta !== undefined ? { meta: body.meta } : {}),
      httpStatus: status,
    });
  }

  return body.data;
}

// ---------------------------------------------------------------------------
// toUiError — classification → UI surface mapping
// ---------------------------------------------------------------------------

/** Where the UI should render the error. */
export type UiErrorSurface = "toast" | "inline";

/**
 * A render-ready, i18n-keyed view of a thrown error. Components render
 * `t(messageKey)` — never a raw server string — so the boundary stays
 * literal-free (ADR-006). `code` is preserved for targeted handling
 * (e.g. a form highlighting the offending field on a `validation` error).
 */
export interface UiError {
  readonly classification: Classification;
  readonly code: string;
  /** i18n key under the `errors.*` namespace; falls back to a generic key. */
  readonly messageKey: string;
  readonly surface: UiErrorSurface;
  readonly retryable: boolean;
  readonly retryAfterMs: number | undefined;
}

/**
 * Spec-58 classification → UI surface. `validation` errors render inline
 * (next to the field/form); everything else is a transient toast. The
 * frontend is never a trust boundary (ADR-008) — `authorization` /
 * `authentication` denials are server-enforced; here we only decide how
 * to *show* the already-decided denial.
 */
const SURFACE_BY_CLASSIFICATION: Readonly<Record<Classification, UiErrorSurface>> = {
  validation: "inline",
  authentication: "toast",
  authorization: "toast",
  not_found: "toast",
  conflict: "inline",
  rate_limited: "toast",
  dependency: "toast",
  unavailable: "toast",
  internal: "toast",
};

/**
 * Map any thrown error into a typed `UiError`. Recognizes `EnvelopeError`
 * (the common case) and degrades unknown throwables to an internal error so
 * the caller's catch path always has a structured, i18n-keyed result.
 */
export function toUiError(error: unknown): UiError {
  if (error instanceof EnvelopeError && error.primary) {
    const { classification, code } = error.primary;
    return {
      classification,
      code,
      // Bundle authors key errors as `errors.code.<code>`; missing keys
      // fall back via i18next to the generic `errors.fallback`.
      messageKey: `errors.code.${code}`,
      surface: SURFACE_BY_CLASSIFICATION[classification] ?? "toast",
      retryable: error.retryable,
      retryAfterMs: error.retryAfterMs,
    };
  }
  return {
    classification: "internal",
    code: SYNTHETIC_ERROR_CODES.INVARIANT,
    messageKey: "errors.fallback",
    surface: "toast",
    retryable: false,
    retryAfterMs: undefined,
  };
}

/**
 * The field paths a validation failure points at ("employees.41.gross_salary_
 * minor_units"), in server order. Spec 58 lets each validation entry carry the
 * field it rejected; the localized message says only "some fields are invalid",
 * so a screen that wants to name the field reads it here. Empty for any other
 * failure, and for a validation the server did not attribute to a field.
 */
export function validationTargets(error: unknown): readonly string[] {
  if (!(error instanceof EnvelopeError)) return [];
  return error.errors.flatMap((entry) =>
    entry.classification === "validation" && entry.target !== undefined && entry.target !== ""
      ? [entry.target]
      : [],
  );
}

// ---------------------------------------------------------------------------
// errorMessage — spec-58 3-tier i18n fallback chain
// ---------------------------------------------------------------------------

/**
 * Resolve a thrown error into a localized, render-ready message string using
 * the spec-58 3-tier fallback chain (most specific → most generic):
 *
 *   1. errors.code.<code>                          — per-code custom message
 *   2. errors.classification.<classification>.fallback — per-classification generic message
 *   3. errors.unknown.fallback                      — terminal fallback
 *
 * `UiError.classification` is never undefined in customer-web (`toUiError()`
 * always resolves to `"internal"` for non-envelope throws), so unlike
 * operator-console's `showErrorToast()` there is no undefined-classification
 * branch to handle here.
 *
 * This is the ONE function components should call to render an error message
 * — replaces the old two-tier `t(toUiError(err).messageKey, { defaultValue:
 * t("errors.fallback") })` pattern, which had no classification-level tier
 * and left the `errors.code.*` bundle bearing the full translation burden for
 * every backend error code.
 */
export function errorMessage(error: unknown, t: TFunction): string {
  const uiError = toUiError(error);
  const classFallbackKey = `errors.classification.${uiError.classification}.fallback`;
  return t(uiError.messageKey, {
    defaultValue: t(classFallbackKey, {
      defaultValue: t("errors.unknown.fallback"),
    }),
  });
}
